import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentUploads: (limit = 10) => api.get(`/uploads/recent?limit=${limit}`),
};

// Upload APIs
export const uploadAPI = {
  uploadFile: (type, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/uploads/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getUploads: (params) => api.get('/uploads', { params }),
  downloadTemplate: (type) => {
    return axios.get(`${API}/templates/${type}`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },
};

// Agent APIs
export const agentAPI = {
  listAgents: () => api.get('/agents/list'),
  runAgent: (agentName, params = {}) => {
    // Send agent_name as query parameter
    return api.post(`/agents/run?agent_name=${agentName}`, params);
  },
  getRuns: (params) => api.get('/agents/runs', { params }),
  getRecommendations: (params) => api.get('/agents/recommendations', { params }),
  downloadReport: (runId) => {
    return axios.get(`${API}/agents/results/${runId}/download`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },
  assignRecommendationsAsTasks: (recommendationIds) => {
    return api.post('/agents/recommendations/assign-tasks', recommendationIds);
  },
  exportRecommendations: (recommendationIds) => {
    return axios.post(`${API}/agents/recommendations/export`, recommendationIds, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
  },
};

// Task APIs
export const taskAPI = {
  createTask: (data) => api.post('/tasks', data),
  updateTask: (taskId, updates) => api.put(`/tasks/${taskId}`, updates),
  getTasks: (params) => api.get('/tasks', { params }),
};

// Chat APIs
export const chatAPI = {
  query: (prompt) => api.post('/chat/query', { prompt }),
};

// User APIs
export const userAPI = {
  getUsers: () => api.get('/users'),
  createUser: (data) => api.post('/tenant/users', data),
};

// Admin APIs
export const adminAPI = {
  createCompany: (data) => api.post('/admin/company', data),
  listCompanies: () => api.get('/admin/companies'),
};